'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarketTradersInfinite } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { TRADERS_PROJECTION } from '@/lib/sdk/projections';
import { cn } from '@/lib/ui/utils';
interface TraderRow {
  trader?: {
    walletAddress?: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    avatarNsfw?: boolean | null;
  };
}
const MAX_AVATARS = 3;
const countFormatterCache: Map<string, Intl.NumberFormat> = new Map();
function getCountFormatter(locale: string): Intl.NumberFormat {
  const cached = countFormatterCache.get(locale);
  if (cached !== undefined) return cached;
  const fmt = new Intl.NumberFormat(locale);
  countFormatterCache.set(locale, fmt);
  return fmt;
}

/**
 * "{count} users participated in this debate" + an overlapping avatar pile of
 * the parent market's top traders (Figma 3916-2758). Sits at the bottom of the
 * winner-page About-token card. `count` is the market's participant tally.
 */
export function WinnerParticipants(t0) {
  const $ = _c(19);
  const {
    marketPubkey,
    count
  } = t0;
  const t = useTranslations("market.winnerDetail");
  const locale = useLocale();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...TRADERS_PROJECTION];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey,
      sortBy: "volume",
      sortOrder: "desc",
      fields: t1
    };
    $[1] = marketPubkey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = useGetMarketTradersInfinite(t2);
  const t3 = query.rows as ReadonlyArray<TraderRow>;
  let t4;
  let t5;
  if ($[3] !== count || $[4] !== locale || $[5] !== t || $[6] !== t3) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const traders = t3.map(_temp).filter(_temp2).slice(0, MAX_AVATARS);
      if (count <= 0) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[9] !== count || $[10] !== locale) {
        t6 = getCountFormatter(locale).format(Math.round(count));
        $[9] = count;
        $[10] = locale;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      let t7;
      if ($[12] !== t6) {
        t7 = <span className="font-medium text-foreground-secondary">{t6}</span>;
        $[12] = t6;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      let t8;
      if ($[14] !== t) {
        t8 = t("participatedLabel");
        $[14] = t;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      let t9;
      if ($[16] !== t7 || $[17] !== t8) {
        t9 = <p className="min-w-0 text-body text-foreground-tertiary">{t7}{" "}{t8}</p>;
        $[16] = t7;
        $[17] = t8;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t4 = <div className="flex items-center justify-between gap-3">{t9}{traders.length > 0 ? <div className="flex shrink-0 items-center">{traders.map(_temp3)}</div> : null}</div>;
    }
    $[3] = count;
    $[4] = locale;
    $[5] = t;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  return t4;
}
function _temp3(trader, idx) {
  return <span key={trader.walletAddress} className={cn(idx > 0 && "-ml-2")}><UserAvatar className="size-6" size="sm" userId={trader.walletAddress} displayName={trader.displayName ?? ""} avatarUrl={trader.avatarUrl ?? null} nsfw={trader.avatarNsfw === true} rasterise={false} borderWidth={1} borderColor="border-background-secondary" /></span>;
}
function _temp2(x) {
  return x !== undefined && typeof x.walletAddress === "string" && x.walletAddress !== "";
}
function _temp(r) {
  return r.trader;
}